<template>
  <div class="bg-light-white">
    <GoBackButtonComponent :path="'/login'" />
  </div>
  
  <div class="h-screen bg-light-white flex flex-col items-center">
    <section class="m-auto px-2 md:px-0 text-secondary" :class="classToUse">
      <LoadingDialog :visible="loading" />
      
      <div class="rounded shadow p-3 text-center bg-white pb-10">
        <LogoComponent :customClass="'text-4xl md:text-6xl'" />
        
        <div v-if="!emailSent">
          <p class="text-2xl font-bold py-4">
            {{ translate('Reset your password', 'ucfirst') }}
          </p>
          <p class="text-gray-600 mb-6 text-sm">
            {{ translate('Enter your email address and we will send you a code to reset your password.', 'ucfirst') }}
          </p>

          <div class="flex flex-col text-left gap-2 mb-6">
            <span class="text-gray-600 block text-sm font-medium">{{ translate('your email', 'ucfirst') }}:</span>
            <InputText 
              v-model="userEmail"
              class="w-full py-2 px-3 rounded-lg border border-color-iron border-focus-purple-suit"
              type="email"
              :placeholder="translate('email', 'ucfirst')"
              :disabled="loading"
            />
          </div>

          <Button 
            class="w-full bg-purple-suit-forced text-white p-1"
            :label="translate('send recovery code', 'capitalize')"
            @click.prevent="submitRequestLink()"
            :loading="loading"
            :disabled="!isEmailValid()"
          />
        </div>

        <div v-else>
          <p class="text-2xl font-bold py-4">
            {{ translate('Enter the recovery code') }}
          </p>
          
          <p class="text-gray-600">
            {{ translate('We sent a recovery code to') }}:<br>
            <strong class="text-secondary">{{ userEmail }}</strong>
          </p>

          <div class="flex justify-center my-8">
            <InputOtp 
              v-model="verificationCode" 
              :length="6"
              size="large"
            />
          </div>

          <Button 
            class="mt-5 w-full bg-purple-suit-forced text-white p-1"
            :label="translate('verify code', 'capitalize')"
            @click.prevent="submitVerifyCode()"
            :loading="loading"
            :disabled="!isVerificationCodeValid()"
          />

          <Button 
            class="mt-5 w-full no-bg-button border text-white p-1" 
            v-if="canResendEmail()" 
            @click="submitRequestLink(true)"
          >
            <p class="text-secondary ml-2">
              {{ translate('resend code', 'capitalize') }}
            </p>
          </Button>
          <p v-if="remainingTime && remainingTime > 0" class="mt-5 text-sm text-gray-500">
            {{ translate('You can resend in') }} {{ remainingTime }}s
          </p>
        </div>

        <p class="mt-8 fill-text-line text-secondary opacity-50 text-xs">
          {{ translate('check your spam folder if you do not see the email.', 'ucfirst') }}
        </p>

        <p class="mt-5 cursor-pointer text-sm font-medium hover:underline transition">
          <router-link :to="'/login'">
            {{ translate('back to login', 'ucfirst') }}
          </router-link>
        </p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import Button from 'primevue/button';
import InputOtp from 'primevue/inputotp';
import InputText from 'primevue/inputtext';
import { translate } from '@/composables/translate';
import { useRoute } from 'vue-router';
import { isScreeSmall } from '@/composables/windowSize';
import cookiesHandler from '@/composables/cookiesHandler';
import DateHandler from '@/composables/handlers/DateHandler';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import UserService from '@/services/UserService';
import { showToast } from '@/composables/toast';
import { useToast } from 'primevue/usetoast';
import urlHandler from '@/composables/urlHandler';
import GoBackButtonComponent from '@/components/misc/GoBackButtonComponent.vue';
import LogoComponent from '@/components/LogoComponent.vue';

const route = useRoute();
const toast = useToast();

const breakPoint = 500;
const userEmail = ref('');
const loading = ref(false);
const emailSent = ref(false);
const verificationCode = ref('');
const remainingTime = ref(0);
let timerInterval = null;

onMounted(() => {
    checkParameters();
    window.addEventListener('resize', updateScreenSize);
    updateScreenSize();
    checkResendTimer();
});

onUnmounted(() => {
    window.removeEventListener('resize', updateScreenSize);
    if (timerInterval) clearInterval(timerInterval);
});

const classToUse = ref('');
const screenSize = ref(window.innerWidth);
const updateScreenSize = () => {
    screenSize.value = window.innerWidth;
    classToUse.value = isScreeSmall(breakPoint) ? 'w-full' : 'm-form-w';
};

function isEmailValid() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(userEmail.value);
}

function isVerificationCodeValid() {
    return verificationCode.value && verificationCode.value.length === 6;
}

function canResendEmail() {
  return remainingTime.value <= 0;
}

function checkResendTimer() {
    const lastSent = cookiesHandler.get('TM_PWD_RESET_SENT');
    if (lastSent) {
        const diffInSeconds = DateHandler.diffInSeconds(lastSent);
        
        if (diffInSeconds < 180) {
          remainingTime.value = 180 - diffInSeconds;
          startCountdown();
        }
    }
}

function startCountdown() {
    if (timerInterval) clearInterval(timerInterval);
    timerInterval = setInterval(() => {
        if (remainingTime.value > 0) {
          remainingTime.value--;
        } else {
          clearInterval(timerInterval);
        }
    }, 1000);
}

const submitRequestLink = async (isResend = false) => {
    if (!isEmailValid()) return;

    loading.value = true;
    const response = await UserService.requestPasswordReset(userEmail.value);
    loading.value = false;

    if (!response.success()) {
      showToast(toast, translate(response.getMessage(), 'ucfirst'), 'error');
      remainingTime.value = response.getContent()?.seconds || 360;
      startCountdown();
      emailSent.value = true;
      return;
    }

    emailSent.value = true;
    cookiesHandler.save('TM_PWD_RESET_SENT', DateHandler.now(), 3);
    remainingTime.value = 180;
    startCountdown();

    showToast(toast, translate(isResend ? 'new code resent successfully' : 'recovery code sent successfully', 'ucfirst'), 'success');
};

// Fase 2: Verifica o código OTP digitado para liberar a redefinição
const submitVerifyCode = async () => {
    if (!isVerificationCodeValid()) return;

    loading.value = true;
    const response = await UserService.verifyPasswordResetCode(userEmail.value, verificationCode.value);
    loading.value = false;

    if (!response.success()) {
      showToast(toast, translate(response.getMessage(), 'ucfirst'), 'error');
      return;
    }

    showToast(toast, translate('code verified, type your new password', 'ucfirst'));
    urlHandler.redirectAndScroll('/reset-password', {
      email: userEmail.value,
      code: verificationCode.value
    });
};

function checkParameters() {
    const email = route.query?.email || null;
    const code = route.query?.code || null;

    if (email) {
        userEmail.value = email;
    }
    if (code) {
        verificationCode.value = code;
        emailSent.value = true;
    }
}
</script>

<style scoped>
.fill-text-line {
    display: flex;
    align-items: center;
    text-align: center;
}
.fill-text-line::before, .fill-text-line::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid rgba(0,0,0,0.1);
}
.fill-text-line:not(:empty)::before { margin-right: .5em; }
.fill-text-line:not(:empty)::after { margin-left: .5em; }
</style>